'use client';

import Cookies from 'js-cookie';
import { Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useState } from 'react';

// Define theme labels as constants
const THEME_LABELS = {
  light: 'Light mode',
  dark: 'Dark mode',
} as const;

// Cookie configuration for cross-subdomain theme sharing
const THEME_COOKIE_KEY = 'theme-preference';

function getCookieDomain(): string | undefined {
  if (typeof window === 'undefined') return undefined;

  // Extract domain from any of the frontend URLs in environment
  const frontendUrls = [
    process.env.NEXT_PUBLIC_FE_URL_AUTH,
    process.env.NEXT_PUBLIC_FE_URL_WALLET,
    process.env.NEXT_PUBLIC_FE_URL_EOR,
    process.env.NEXT_PUBLIC_FE_URL_ADMIN,
  ].filter(Boolean);

  // Parse domain from environment URLs
  for (const url of frontendUrls) {
    if (url) {
      try {
        const urlObj = new URL(url);
        const hostname = urlObj.hostname;

        // Skip localhost/development
        if (hostname === 'localhost' || hostname.includes('127.0.0.1')) {
          continue;
        }

        // Extract root domain for cross-subdomain sharing
        const parts = hostname.split('.');
        if (parts.length >= 2) {
          // Return root domain with leading dot for cross-subdomain sharing
          // e.g., "wallet.teams.novp.com" -> ".teams.novp.com"
          // e.g., "auth-stage.nativeteams.com" -> ".nativeteams.com"
          const rootDomain = parts.slice(-2).join('.');
          return `.${rootDomain}`;
        }
      } catch {}
    }
  }

  // Fallback to current hostname analysis if no env URLs available
  const currentHostname = window.location.hostname;

  if (currentHostname === 'localhost' || currentHostname.includes('127.0.0.1')) {
    return undefined; // Don't set domain for localhost
  }

  const parts = currentHostname.split('.');
  if (parts.length >= 2) {
    const rootDomain = parts.slice(-2).join('.');
    return `.${rootDomain}`;
  }

  return undefined;
}

export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Sync theme with cookies for cross-domain sharing
  const syncThemeWithCookie = useCallback((newTheme: string) => {
    const domain = getCookieDomain();

    try {
      if (domain) {
        Cookies.set(THEME_COOKIE_KEY, newTheme, {
          expires: 365, // 1 year
          domain,
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
        });
      } else {
        Cookies.set(THEME_COOKIE_KEY, newTheme, {
          expires: 365,
          sameSite: 'lax',
          secure: window.location.protocol === 'https:',
        });
      }
    } catch (error) {
      console.warn('Failed to set theme cookie:', error);
    }
  }, []);

  // Load theme from cookie on mount
  useEffect(() => {
    setMounted(true);

    try {
      const cookieTheme = Cookies.get(THEME_COOKIE_KEY);
      if (cookieTheme && (cookieTheme === 'light' || cookieTheme === 'dark')) {
        // Only set if different from current theme to avoid unnecessary updates
        if (cookieTheme !== resolvedTheme && cookieTheme !== theme) {
          setTheme(cookieTheme);
        }
      }
    } catch (error) {
      console.warn('Failed to read theme cookie:', error);
    }
  }, [setTheme, theme, resolvedTheme]);

  const handleThemeToggle = useCallback(() => {
    const currentTheme = resolvedTheme ?? theme;
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';

    // Set theme via next-themes
    setTheme(newTheme);

    // Sync with cookie for cross-domain sharing
    syncThemeWithCookie(newTheme);
  }, [theme, resolvedTheme, setTheme, syncThemeWithCookie]);

  if (!mounted) {
    return null;
  }

  return (
    <button
      className="h-9 group peer/menu-button text-sidebar-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground flex w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-hidden transition-[width,height,padding] group-has-data-[sidebar=menu-action]/menu-item:pr-8 group-data-[collapsible=icon]:size-10! group-data-[collapsible=icon]:p-2! focus-visible:ring-2 disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-[24px] [&>svg]:shrink-0 lg:[&>svg]:size-6"
      onClick={handleThemeToggle}
    >
      {(resolvedTheme ?? theme) === 'light' ? (
        <Moon className="h-5 w-5" />
      ) : (
        <Sun className="h-5 w-5" />
      )}
      <span className="text-sidebar-foreground">
        {(resolvedTheme ?? theme) === 'light' ? THEME_LABELS.dark : THEME_LABELS.light}
      </span>
    </button>
  );
}
